import { useCallback } from 'react'
import { useNavigate } from 'react-router-dom'
import { captureException } from '@sentry/react'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { chatConversationsQueryKey } from '../../../../api/chat'
import { useToastNotifications } from '../../../../hooks/useToastNotification'
import { reverseUrl } from '../../../../navigation/reverseUrl'
import { useSelectedChatId } from '../../../../state/context/SelectedChatIdProvider'
import { ToastType } from '../../../common/Toast/toast-type'
import { removeRoomQueries } from '../useRoomMessagesCache'

// Leaves the room view after the viewer lost or gave up access to a room.
export const useExitRoom = (): ((roomId: string) => void) => {
  const client = useQueryClient()
  const navigate = useNavigate()
  const { setSelectedChatRoomId } = useSelectedChatId()

  return useCallback(
    (roomId: string) => {
      removeRoomQueries(client, roomId)
      client.invalidateQueries({ queryKey: chatConversationsQueryKey })
      setSelectedChatRoomId(undefined)
      navigate(reverseUrl('chat'))
    },
    [client, navigate, setSelectedChatRoomId]
  )
}

interface RoomExitMutationOptions {
  roomId: string
  mutationFn: () => Promise<unknown>
  successMessage: string
  errorMessage: string
}

export const useRoomExitMutation = ({
  roomId,
  mutationFn,
  successMessage,
  errorMessage,
}: RoomExitMutationOptions): (() => void) => {
  const { setToastNotification } = useToastNotifications()
  const exitRoom = useExitRoom()

  const { mutate } = useMutation({
    mutationFn,
    meta: { skipGlobalInvalidate: true },
    onSuccess: () => {
      setToastNotification(ToastType.SUCCESS, successMessage)
      exitRoom(roomId)
    },
    onError: (error) => {
      setToastNotification(ToastType.DANGER, errorMessage)
      captureException(error)
    },
  })
  return mutate
}
